/**
 * 크레딧 — 사장님 크레딧 PG 결제 (토스페이먼츠) API 클라이언트
 *
 *  BE 엔드포인트(/business/wallet/charge/*) 와 1:1 매핑.
 *  ⚠ 기존 사용자(B2C) 멍코인 결제 API 와 완전 분리.
 */
import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import type { BaseApiResponse } from "~/types/common";

/* ─────────────────────────────────────────────────────────────────
 *  타입 정의 — BE DTO 와 1:1 매핑
 * ───────────────────────────────────────────────────────────────── */

export interface BuyCreditPreOrderRequest {
  packageSeq: number;
}

export interface BuyCreditPreOrderResponse {
  purchaseCreditTempSeq: number;
  companySeq: number;
  bizUserSeq: number;
  packageSeq: number;
  orderId: string;
  orderName: string;
  customerKey: string;
  customerEmail?: string | null;
  customerName?: string | null;
  payAmount: number;       // 결제 금액 (원)
  creditAmount: number;    // 받을 크레딧
  discount: number;
  widgetClientKey: string; // 토스 client key
  paymentStatus: string;   // PENDING
  message: string;
}

export interface BuyCreditConfirmRequest {
  paymentKey: string;
  orderId: string;
  amount: number;
}

export interface BuyCreditConfirmResponse {
  purchaseCreditSeq: number;
  companySeq: number;
  bizUserSeq: number;
  orderId: string;
  paymentKey: string;
  payAmount: number;
  creditAmount: number;
  balanceAfter: number;
  paidBalanceAfter: number;
  creditTxSeq: number;
  /**
   * COMPLETED       — 즉시 승인 (카드/간편결제/계좌이체). 크레딧 적립 완료.
   * WAITING_DEPOSIT — 가상계좌 발급됨. 아직 입금 전이라 적립되지 않았고,
   *                   입금이 확인되면 서버가 웹훅을 받아 자동으로 충전한다.
   */
  paymentStatus: string;
  paymentMethod?: string | null;
  pgReceiptUrl?: string | null;
  message: string;

  /* ── 가상계좌 입금 안내 (paymentStatus === "WAITING_DEPOSIT" 일 때만) ── */
  virtualAccountBankName?: string | null;
  virtualAccountNumber?: string | null;
  virtualAccountHolder?: string | null;
  dtDepositDue?: string | null;
}

export interface PaymentHistoryItem {
  purchaseCreditSeq: number;
  orderId: string;
  paymentKey: string;
  orderName: string;
  payAmount: number;
  creditAmount: number;
  /** COMPLETED / CANCELLED / REFUNDED / PARTIAL_REFUNDED / WAITING_DEPOSIT */
  paymentStatus: string;
  paymentMethod?: string | null;
  pgReceiptUrl?: string | null;
  dtPaid: string;
  /** 셀프 100% 취소 가능 여부 — 7일 이내 + 미사용 + COMPLETED. */
  canCancel?: boolean | null;

  /* ── 가상계좌 입금 대기 건 (paymentStatus === "WAITING_DEPOSIT") ── */
  virtualAccountBankName?: string | null;
  virtualAccountNumber?: string | null;
  virtualAccountHolder?: string | null;
  dtDepositDue?: string | null;
}

export interface PaymentHistoryPage {
  content: PaymentHistoryItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  /**
   * 가상계좌 입금 대기 건 — 아직 결제 완료 이력에 없어서 content 와 분리돼 있다.
   * 첫 페이지 응답에만 채워지며 페이징 대상이 아니다.
   */
  waitingDeposits?: PaymentHistoryItem[] | null;
}

/* ─────────────────────────────────────────────────────────────────
 *  HTTP helpers
 * ───────────────────────────────────────────────────────────────── */

const E = API_ENDPOINTS.BUSINESS_WALLET_CHARGE;

const unwrap = <T,>(payload: BaseApiResponse<T> | T | undefined): T => {
  if (payload == null) throw new Error("응답이 비어있습니다");
  if (typeof payload === "object" && payload !== null && "data" in (payload as any)) {
    return (payload as BaseApiResponse<T>).data;
  }
  return payload as T;
};

/* ─────────────────────────────────────────────────────────────────
 *  API
 * ───────────────────────────────────────────────────────────────── */

export const walletChargeApi = {
  /** 사전 주문 — 토스 결제창 호출에 필요한 값 발급. */
  async preOrder(packageSeq: number): Promise<BuyCreditPreOrderResponse> {
    const { data } = await privateHttp.post<BaseApiResponse<BuyCreditPreOrderResponse>>(
      E.PRE_ORDER,
      { packageSeq } as BuyCreditPreOrderRequest,
    );
    return unwrap(data);
  },

  /** 결제 승인 — 토스 successUrl 도착 후 호출, 크레딧 적립. */
  async confirm(req: BuyCreditConfirmRequest): Promise<BuyCreditConfirmResponse> {
    const { data } = await privateHttp.post<BaseApiResponse<BuyCreditConfirmResponse>>(
      E.CONFIRM,
      req,
    );
    return unwrap(data);
  },

  /** 본인 결제 이력 페이지 (최신순). */
  async listOrders(params: { page?: number; size?: number } = {}): Promise<PaymentHistoryPage> {
    const { data } = await privateHttp.get<BaseApiResponse<PaymentHistoryPage>>(E.ORDERS, {
      params: { page: params.page ?? 0, size: params.size ?? 20 },
    });
    return unwrap(data);
  },

  /** 셀프 100% 취소 (7일 이내 + 미사용 + COMPLETED 만). reason 은 선택. */
  async cancelOrder(orderId: string, reason?: string): Promise<void> {
    await privateHttp.post<BaseApiResponse<void>>(E.ORDER_CANCEL(orderId), { reason });
  },
};
